import React, { FC } from 'react';
import { TrainerPackage } from 'src/api/services/packagesService';
import { ReactComponent as ProgramIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/packages-list/dumbbell-program.svg';
import { ReactComponent as ChatIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/packages-list/inquiry-chat.svg';
import { ReactComponent as VideoIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/packages-list/video-checkin.svg';
import './PackagesListView.scss';

interface PackagesListViewProps {
  packages: TrainerPackage[];
  onEditPackage: (pkg: TrainerPackage) => void;
}

export const PackagesListView: FC<PackagesListViewProps> = ({ packages, onEditPackage }) => {
  const tierOrder: Record<string, number> = {
    'Package 1': 1,
    'Package 2': 2,
    'Package 3': 3,
  };

  const sortedPackages = [...packages].sort(
    (a, b) => (tierOrder[a.presetTier] || 99) - (tierOrder[b.presetTier] || 99),
  );

  return (
    <div className="packages-list-view">
      {/* Package cards */}
      <div className="packages-cards-stack">
        {sortedPackages.map((pkg) => {
          const isPublished = pkg.status === 'published';
          const inquiriesCount = pkg.coaching?.inquiriesPerWeek ?? 1;
          const checkinsCount = pkg.coaching?.checkInsPerWeek ?? 2;

          return (
            <div key={pkg.id} className="package-item-card">
              {/* Card Header */}
              <div className="package-card-header">
                <div className="package-title-col">
                  <div className="package-name-row">
                    <h2 className="package-name">{pkg.name}</h2>
                    <span className={`package-status-badge ${isPublished ? 'published' : 'draft'}`}>
                      {isPublished ? 'PUBLISHED' : 'SAVED AS DRAFT'}
                    </span>
                  </div>
                  <p className="package-short-desc">
                    {pkg.shortDescription || 'Build strength with personal support'}
                  </p>
                </div>

                <div className="package-price-col">
                  <div className="price-amount-row">
                    <span className="price-val">
                      {pkg.currencySymbol || '£'}
                      {pkg.price}
                    </span>
                    <span className="price-period">{' / month'}</span>
                  </div>
                  <span className="renewal-note">
                    {pkg.terms?.renewal || 'No automatic renewal'}
                  </span>
                </div>
              </div>

              {/* Feature Tiles Grid */}
              <div className="package-features-grid">
                <div className="feature-tile-box">
                  <div className="feature-icon-wrapper">
                    <ProgramIcon className="tile-svg" />
                  </div>
                  <div className="feature-tile-text">
                    <h4 className="feature-tile-title">{'Personalized training program'}</h4>
                    <p className="feature-tile-subtitle">
                      {'A structured plan tailored to your goals.'}
                    </p>
                  </div>
                </div>

                <div className="feature-tile-box">
                  <div className="feature-icon-wrapper">
                    <ChatIcon className="tile-svg" />
                  </div>
                  <div className="feature-tile-text">
                    <h4 className="feature-tile-title">
                      {inquiriesCount === 1
                        ? '1 inquirie / week'
                        : `${inquiriesCount} inquiries / week`}
                    </h4>
                    <p className="feature-tile-subtitle">{'Detailed feedback on your progress'}</p>
                  </div>
                </div>

                <div className="feature-tile-box">
                  <div className="feature-icon-wrapper">
                    <VideoIcon className="tile-svg" />
                  </div>
                  <div className="feature-tile-text">
                    <h4 className="feature-tile-title">
                      {`${checkinsCount} x 15-min video check-ins`}
                    </h4>
                    <p className="feature-tile-subtitle">{'Stay on track with regular support.'}</p>
                  </div>
                </div>
              </div>

              {/* Edit Package Button */}
              <div className="package-card-action">
                <button
                  type="button"
                  className="btn-edit-package-white"
                  onClick={() => onEditPackage(pkg)}
                >
                  {'Edit Package'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
